import { useEffect, useMemo, useRef } from "react";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { useAutosave, useEngineBinding, useSolidPreview } from "@/hooks/useEngineBinding";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useStlExport } from "@/hooks/useStlExport";
import { Viewport } from "@/scene/Viewport";
import type { BrushUniforms } from "@/scene/holdMaterial";
import { isCuttingTool, useDesignerStore } from "@/state/designerStore";
import { CutOverlay } from "./CutOverlay";
import {
  Diagnostics,
  ExportStatusLine,
  FlangePreviewDialog,
  NewDesignDialog,
  OverallSmoothDialog,
  ResetDialog,
  ShortcutsDialog,
} from "./Overlays";
import { ToolPanel } from "./ToolPanel";
import { TopBar } from "./TopBar";
import { ViewSwitcher } from "./ViewSwitcher";
import { NavigationDock } from "./NavigationDock";

interface WorkspaceProps {
  engine: DesignerEngine;
  uniforms: BrushUniforms;
  onContextLost: () => void;
  onNewDesign: () => void;
  onConfirmNewDesign: () => void;
}

export function Workspace({
  engine,
  uniforms,
  onContextLost,
  onNewDesign,
  onConfirmNewDesign,
}: WorkspaceProps): React.ReactElement {
  const tool = useDesignerStore((s) => s.tool);
  const navigationMode = useDesignerStore((s) => s.navigationMode);
  const exportStl = useStlExport(engine);
  const cutRunner = useRef<(() => void) | null>(null);

  useEngineBinding(engine);
  useSolidPreview(engine);
  useAutosave(engine);
  useKeyboardShortcuts({ engine, onExport: () => void exportStl(), cutRunner });

  // Enable diagnostics from the URL in any build, for on-site testing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("dev")) useDesignerStore.getState().toggleDiagnostics();
  }, []);

  const viewportClass = useMemo(() => {
    if (navigationMode !== "sculpt") return "viewport viewport--navigation";
    if (tool === "fixings") return "viewport viewport--placing";
    if (isCuttingTool(tool)) return "viewport viewport--cutting";
    return "viewport viewport--carving";
  }, [tool, navigationMode]);

  return (
    <div className="workspace">
      <div className={viewportClass}>
        <Viewport
          engine={engine}
          uniforms={uniforms}
          onContextLost={onContextLost}
          cutRunner={cutRunner}
        />
        <CutOverlay />
      </div>

      <div className="hud">
        <TopBar engine={engine} onExport={() => void exportStl()} onNew={onNewDesign} />

        <div className="hud__row hud__row--bottom">
          <ToolPanel engine={engine} cutRunner={cutRunner} />
          <div className="hud__bottom-right">
            <Diagnostics engine={engine} />
            <ExportStatusLine />
            <ViewSwitcher />
          </div>
        </div>
      </div>

      <NavigationDock />

      <ResetDialog engine={engine} />
      <NewDesignDialog onConfirm={onConfirmNewDesign} />
      <ShortcutsDialog />
      <OverallSmoothDialog engine={engine} />
      <FlangePreviewDialog engine={engine} />
    </div>
  );
}
